import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
  ViewportRuler,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  DOCUMENT,
  ElementRef,
  inject,
  Injectable,
  Injector,
  Renderer2,
  RendererFactory2,
  signal,
  WritableSignal,
} from '@angular/core';
import { environment } from '@environments/environment';
import { DialogRef } from '@shared/classes/dialog.class';
import { DeviceType, DialogPositionStrategyInIframe } from '@shared/constants';
import {
  ComponentType,
  DIALOG_DATA,
  DialogConfig,
  DialogPosition,
  LoyaltyChallengeConfig,
} from '@shared/models';

import { DataService } from './data.service';
import { IframeTools } from './iframe-tools.service';
import { MediaObserverService } from './media-observer.service';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly overlay: Overlay = inject(Overlay);
  private readonly document: Document = inject(DOCUMENT);
  private readonly injector: Injector = inject(Injector);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly iframeTools: IframeTools = inject(IframeTools);
  private readonly dataService: DataService = inject(DataService);
  private readonly mediaObserverService = inject(MediaObserverService);
  private readonly viewportRuler: ViewportRuler = inject(ViewportRuler);
  public iframeScrollPos: WritableSignal<number> = signal(0);
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  private readonly modalsHeight: Record<string, number> = {
    select: 400,
    scanner: 600,
    unselect: 430,
  };

  private firstOverlayRef?: OverlayRef;
  private secondaryOverlayRef?: OverlayRef;
  private stopParentListener?: () => void;

  private parentScrollY = 0;
  private parentInnerHeight = 0;
  private firstOverlayAnchoredToBottom = false;

  private startParentScrollSync() {
    this.document.defaultView?.parent?.postMessage('subscribe-scroll', '*');

    this.stopParentListener = this.renderer.listen(
      'window',
      'message',
      (event) => {
        if (!event?.data) return;

        if (event.data.id === 'y-scroll-position') {
          this.parentScrollY = event.data.scrollY ?? 0;
          this.parentInnerHeight =
            event.data.innerHeight ?? this.parentInnerHeight;
          this.iframeScrollPos.set(this.parentScrollY);
          this.updateActiveOverlayPosition();
        }

        if (event.data.id === 'parent-scroll') {
          this.parentScrollY = event.data.scrollY ?? 0;
          this.parentInnerHeight = event.data.innerHeight ?? 0;
          this.iframeScrollPos.set(this.parentScrollY);
          this.updateActiveOverlayPosition();
        }

        if (event.data.id === 'parent-resize') {
          this.parentInnerHeight =
            event.data.innerHeight ?? this.parentInnerHeight;
          this.updateActiveOverlayPosition();
        }
      }
    );
  }

  // Embedded hosts (native WebView wrappers, iframe parents) own the vertical
  // scroll: the CDK block() strategy cannot stop them, and on Android a native
  // scroll container intercepts touch gestures before the web engine sees
  // them, making inner dialog scrollables unreachable. Announcing modal
  // open/close lets the host freeze its scrolling while a dialog is up.
  private notifyParentModalState(open: boolean) {
    if (!this.iframeTools.isInIframe) return;
    this.document.defaultView?.parent?.postMessage(
      open ? 'modal-open' : 'modal-close',
      '*'
    );
  }

  private stopParentScrollSync() {
    if (this.stopParentListener) {
      this.document.defaultView?.parent?.postMessage('unsubscribe-scroll', '*');
      this.stopParentListener();
      this.stopParentListener = undefined;
    }
  }

  private updateActiveOverlayPosition() {
    const pane = this.firstOverlayRef?.overlayElement;
    if (!pane) return;

    const iframeHeight = this.document.defaultView?.innerHeight || 0;
    const translateY = this.firstOverlayAnchoredToBottom
      ? this.bottomSheetOffset(pane, iframeHeight)
      : this.centredDialogOffset(pane, iframeHeight);

    this.document.defaultView?.requestAnimationFrame(() => {
      pane.style.transform = `translateY(${Math.round(translateY)}px)`;
      pane.style.transition = `transform 50ms ease-in-out`;
    });
  }

  private centredDialogOffset(pane: HTMLElement, iframeHeight: number): number {
    const config = this.dataService.config() as LoyaltyChallengeConfig;
    const verticalOffset = config?.dialogVerticalOffset ?? 0;

    const modalType: string | undefined = this.dataService.objectRef()?.type as
      string | undefined;
    const modalHeight = modalType ? this.modalsHeight[modalType] : 400;
    const targetTop =
      this.parentScrollY +
      (this.parentInnerHeight - modalHeight) / 2 +
      verticalOffset;

    const paneHeight = pane.offsetHeight || modalHeight;
    return Math.max(0, Math.min(targetTop, iframeHeight - paneHeight));
  }

  // The sheet is laid out on the iframe's bottom edge (`bottom: 0`). When the
  // iframe runs past the parent viewport, pull it up to the viewport's bottom
  // edge so it stays visible, but never further than its own height allows.
  private bottomSheetOffset(pane: HTMLElement, iframeHeight: number): number {
    const parentInnerHeight = this.parentInnerHeight || iframeHeight;
    const visibleBottom = this.parentScrollY + parentInnerHeight;
    const sheetBottom = Math.max(
      pane.offsetHeight,
      Math.min(visibleBottom, iframeHeight)
    );
    return sheetBottom - iframeHeight;
  }

  private getPositionStrategyName(): DialogPositionStrategyInIframe {
    const searchParams: URLSearchParams = new URLSearchParams(
      this.document.defaultView?.location.search
    );
    let dialogPositionStategy = searchParams.get('dialog_position_strategy');
    if (!dialogPositionStategy) {
      // Temporary: eventually remove this and always use the query param if exists, default otherwise
      dialogPositionStategy =
        environment.dialogPositionStrategyInIframe as string;
    }
    return dialogPositionStategy as DialogPositionStrategyInIframe;
  }

  /**
   * Open a custom component in an overlay.
   * `anchorToBottom` displays it as a bottom sheet on mobile and tablet.
   */
  async open<T>(
    component: ComponentType<T>,
    config?: DialogConfig
  ): Promise<DialogRef> {
    const anchorToBottom = config?.anchorToBottom ?? false;

    // Globally centered position strategy, unless the caller asks for a drawer
    const positionStrategy:
      GlobalPositionStrategy | FlexibleConnectedPositionStrategy =
      await this.getPositionStrategy(anchorToBottom, config?.position);

    const isFirstOverlay = !this.firstOverlayRef;
    const anchoredToBottom = this.isBottomSheet(
      anchorToBottom,
      config?.position
    );

    const panelClass: string[] = [
      ...(anchoredToBottom ? ['bottom-sheet-panel'] : []),
      // A dialog opened on top of another is anchored to it, so it keeps its
      // own size instead of the shared full-width card.
      ...(isFirstOverlay ? [] : ['nested-panel']),
      ...(config?.panelClass ? [config.panelClass] : []),
    ];

    // Create the overlay with customizable options
    const overlayRef = this.overlay.create({
      panelClass,
      positionStrategy,
      backdropClass: 'overlay-backdrop',
      hasBackdrop: isFirstOverlay, // only first overlay has backdrop
      // Prevent background scrolling when the primary dialog is open
      scrollStrategy:
        isFirstOverlay && (config?.blockScroll ?? true)
          ? this.overlay.scrollStrategies.block()
          : this.overlay.scrollStrategies.reposition(),
    });

    if (!this.firstOverlayRef) {
      this.firstOverlayRef = overlayRef;
      this.notifyParentModalState(true);
    } else {
      this.secondaryOverlayRef = overlayRef;
    }

    // Create dialogRef to return
    const dialogRef: DialogRef = new DialogRef(overlayRef);

    // Only the primary overlay follows the parent scroll: dependent overlays
    // are connected to it and the drawer spans the full iframe height.
    const followsParentScroll =
      isFirstOverlay &&
      config?.position !== 'right' &&
      this.iframeTools.isInIframe &&
      this.getPositionStrategyName() ===
        DialogPositionStrategyInIframe.POST_MESSAGE;

    if (followsParentScroll) {
      this.firstOverlayAnchoredToBottom = anchoredToBottom;
      this.startParentScrollSync();
    }

    // Create injector to be able to reference the DialogRef from within the component
    const injector: Injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: config as DialogConfig },
      ],
    });

    // Attach component portal to the overlay
    const portal: ComponentPortal<T> = new ComponentPortal(
      component,
      null,
      injector
    );
    overlayRef.attach(portal);
    overlayRef.backdropClick().subscribe(() => dialogRef.close());

    overlayRef.detachments().subscribe(() => {
      if (overlayRef === this.firstOverlayRef) {
        this.stopParentScrollSync();
        this.firstOverlayRef = undefined;
        this.notifyParentModalState(false);
      } else if (overlayRef === this.secondaryOverlayRef) {
        this.secondaryOverlayRef = undefined;
      }
    });

    return dialogRef;
  }

  /**
   * Whether the dialog is docked to the bottom edge rather than centred. It is
   * a bottom sheet only below desktop, and only where neither the drawer nor a
   * parent dialog already claims the pane — on desktop it is an ordinary
   * centred card and takes the shared card width.
   */
  private isBottomSheet(
    anchorToBottom: boolean,
    position?: DialogPosition
  ): boolean {
    return (
      anchorToBottom &&
      position !== 'right' &&
      !this.firstOverlayRef &&
      this.mediaObserverService.device() !== DeviceType.DESKTOP
    );
  }

  private async getPositionStrategy(
    anchorToBottom: boolean,
    position?: DialogPosition
  ): Promise<GlobalPositionStrategy | FlexibleConnectedPositionStrategy> {
    // A drawer docks to the right edge whatever the surrounding context is, so
    // this wins over the connected/iframe strategies below.
    if (position === 'right') {
      return this.overlay.position().global().right('0').top('0');
    }

    if (this.firstOverlayRef) {
      const originEl = this.firstOverlayRef.overlayElement;
      const ordered = this.orderPositionsBySpace(originEl, 12);

      return this.overlay
        .position()
        .flexibleConnectedTo(originEl)
        .withPositions(ordered)
        .withViewportMargin(10)
        .withPush(true) // push inside viewport instead of clipping
        .withFlexibleDimensions(false); // keep your dependent modal size
    }

    if (this.isBottomSheet(anchorToBottom, position)) {
      return this.overlay
        .position()
        .global()
        .bottom('0px')
        .centerHorizontally();
    }

    const dialogPositionStategyName = this.getPositionStrategyName();
    // Usually we want to keep the default strategy in mobile apps but we want to apply custom strategies (click or post_message) in websites

    if (this.iframeTools.isInIframe) {
      if (dialogPositionStategyName === DialogPositionStrategyInIframe.CLICK) {
        const objectRef = this.dataService.objectRef();
        const elementRef: ElementRef = objectRef?.element as ElementRef;
        const modalType: string = objectRef?.type as string;
        const modalHeight: number = this.modalsHeight[modalType];

        let originY = 300;
        if (elementRef) {
          const { top } = elementRef.nativeElement.getBoundingClientRect();
          originY = top - modalHeight / 2;
        }

        return this.overlay
          .position()
          .global()
          .centerHorizontally()
          .top(originY - 200 + 'px');
      } else if (
        dialogPositionStategyName ===
        DialogPositionStrategyInIframe.POST_MESSAGE
      ) {
        await this.requestParentScrollPosition();

        this.parentInnerHeight =
          this.parentInnerHeight || this.document.defaultView?.innerHeight || 0;

        const positionStrategy = this.overlay.position().global();
        positionStrategy.centerHorizontally();
        positionStrategy.top('0');

        return positionStrategy;
      }
    }

    return this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
  }

  requestParentScrollPosition(): Promise<void> {
    return new Promise((resolve) => {
      this.document.defaultView?.parent?.postMessage(
        'get-scroll-position',
        '*'
      );

      const listener = this.renderer.listen('window', 'message', (event) => {
        if (
          (event.data && event.data.id === 'y-scroll-position') ||
          event.data.id === 'parent-scroll'
        ) {
          const yScrollPosition = event.data.scrollY;
          const innerH = event.data.innerHeight ?? 0;

          this.parentScrollY = yScrollPosition;
          this.parentInnerHeight = innerH || this.parentInnerHeight;
          this.iframeScrollPos.set(yScrollPosition);

          listener();
          resolve();
        }
      });
    });
  }

  private orderPositionsBySpace(
    originEl: HTMLElement,
    offset = 12
  ): ConnectedPosition[] {
    const viewport = this.viewportRuler.getViewportSize();
    const rect = originEl.getBoundingClientRect();

    const space = {
      right: viewport.width - rect.right,
      left: rect.left,
      bottom: viewport.height - rect.bottom,
      top: rect.top,
    };

    type Dir = 'right' | 'left' | 'bottom' | 'top';
    const baseOrder: Dir[] = ['right', 'left', 'bottom', 'top']; // tie-break preference

    const orderedDirs = [...baseOrder].sort((a, b) => {
      const diff = space[b] - space[a];
      return diff !== 0 ? diff : baseOrder.indexOf(a) - baseOrder.indexOf(b);
    });

    const map: Record<Dir, ConnectedPosition> = {
      right: {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetX: offset,
      },
      left: {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
        offsetX: -offset,
      },
      bottom: {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: offset,
      },
      top: {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -offset,
      },
    };

    // Primary order by available space; then add a safety loop of all as final fallbacks
    const positions = orderedDirs.map((d) => map[d]);
    positions.push(map.right, map.left, map.bottom, map.top);
    return positions;
  }
}
